import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { XIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { Loading } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import { getMicrosoftFilesSourceLabel, MICROSOFT_FILES_OAUTH_PROVIDER_KEY } from '@/lib/microsoftFiles';
import { useMatterLabels } from '@/lib/useMatterLabels';

export default function OneDriveAuthCallback() {
  const navigate = useNavigate();
  const {
    code,
    state,
    error: callbackError,
    error_description: errorDescription,
  } = useSearch({ from: '/onedrive-auth-callback' });
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Exchanging authorization code...');
  const [sourceLabel, setSourceLabel] = useState('Microsoft Source');
  const label = useMatterLabels();
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    const exchangeCodeForToken = async () => {
      const storedState = sessionStorage.getItem('onedrive_oauth_state');
      const storedProvider = sessionStorage.getItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY);
      const nextSourceLabel = getMicrosoftFilesSourceLabel(storedProvider);
      setSourceLabel(nextSourceLabel);

      if (callbackError) {
        sessionStorage.removeItem('onedrive_oauth_state');
        sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY);
        setError(errorDescription || callbackError);
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setIsProcessing(false);
        return;
      }

      if (!state || !storedState || state !== storedState) {
        sessionStorage.removeItem('onedrive_oauth_state');
        sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY);
        setError('Invalid or expired authentication state. Please try connecting again.');
        setIsProcessing(false);
        return;
      }

      try {
        setStatusMessage(`Connecting to ${nextSourceLabel}...`);

        const response = await backendClient.post('/onedrive/oauth/token', {
          code,
          state
        });
        sessionStorage.removeItem('onedrive_oauth_state');
        sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY);

        setStatusMessage(labelRef.current('finalising-authentication'));

        if (response.data.access_token && response.data.oauth_account_id) {
          const authData = {
            access_token: response.data.access_token,
            oauth_account_id: response.data.oauth_account_id,
            expires_in: response.data.expires_in || 3600,
            token_type: response.data.token_type || 'Bearer'
          };
          setStatusMessage('Authentication complete!');

          if (window.opener && !window.opener.closed) {
            try {
              window.opener.postMessage(
                {
                  type: 'onedrive-auth-success',
                  authData
                },
                window.location.origin
              );

              let acknowledged = false;
              const handleAck = (event: MessageEvent) => {
                if (event.origin === window.location.origin && event.data.type === 'onedrive-auth-acknowledged') {
                  acknowledged = true;
                  window.removeEventListener('message', handleAck);
                  setIsProcessing(false);
                  setTimeout(() => {
                    window.close();
                  }, 500);
                }
              };

              window.addEventListener('message', handleAck);

              setTimeout(() => {
                if (!acknowledged) {
                  window.removeEventListener('message', handleAck);
                  setIsProcessing(false);
                  window.close();
                }
              }, 10000);
            } catch {
              setIsProcessing(false);
              setTimeout(() => {
                navigate({ to: '/' });
              }, 2000);
            }
          } else {
            setIsProcessing(false);
            setTimeout(() => {
              navigate({ to: '/' });
            }, 2000);
          }
        } else {
          setError('Failed to exchange code for token');
          setIsProcessing(false);
        }
      } catch (err: unknown) {
        sessionStorage.removeItem('onedrive_oauth_state');
        sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY);
        const message = apiErrorMessage(err) || (err instanceof Error ? err.message : 'Failed to authenticate');
        toast.error(message);
        setError(message);
        setIsProcessing(false);
      }
    };

    const timeoutId = setTimeout(() => {
      exchangeCodeForToken();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [callbackError, code, errorDescription, navigate, state]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <Button type="button" icon={XIcon} text="Close Window" onClick={() => window.close()} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md">
        {isProcessing && (
          <>
            <Loading />
            <h2 className="text-lg font-semibold text-gray-900">Completing {sourceLabel} authentication...</h2>
            <p className="text-sm text-gray-600 mt-2">{statusMessage}</p>
            <p className="text-xs text-gray-500 mt-4">Please do not close this window</p>
          </>
        )}
      </div>
    </div>
  );
}
